import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import {
  AvailabilityMap,
  InstructorDto,
  LANGUAGE_CODES,
  LanguageLevel,
  LessonDto,
  LessonStatus,
  LICENCE_DISCIPLINES,
  LicenceLevel,
} from '../../../service/types'
import { TIME_SLOTS } from '../constants'
import { LessonPreview } from '../LessonDialog'
import { isSlotAvailable } from './availability'
import { DragGhost } from './DragGhost'
import { LessonBlockContent } from './LessonBlockContent'
import * as styles from './styles'
import { useCurrentTime } from './useCurrentTime'
import { useLessonDrag } from './useLessonDrag'

const getPaidRatio = (bookings: { customerId: string; receiptId: string | null }[]): number | undefined => {
  if (bookings.length === 0) return undefined
  const byCustomer = new Map<string, boolean>()
  for (const b of bookings) {
    const prev = byCustomer.get(b.customerId)
    // A customer is "paid" only if ALL their bookings have a receiptId
    byCustomer.set(b.customerId, (prev ?? true) && !!b.receiptId)
  }
  const customers = [...byCustomer.values()]
  const paidCount = customers.filter(Boolean).length
  if (paidCount === 0 || paidCount === customers.length) return undefined
  return paidCount / customers.length
}

type Props = {
  isToday?: boolean
  instructors: InstructorDto[]
  lessons: LessonDto[]
  availabilityMap: AvailabilityMap
  selectedLessonId?: string
  onLessonClick: (lesson: LessonDto) => void
  onEmptyCellClick: (instructorId: string, time: string) => void
  onLessonDrop: (
    lessonId: string,
    sourceInstructorId: string | null,
    targetInstructorId: string | null,
    time: string,
  ) => void
  lessonPreview?: LessonPreview
  pendingLessonIds?: Set<string>
}

const SLOT_WIDTH = 90
const UNASSIGNED_LESSON_HEIGHT = 68
const GRID_START_MINUTES = 8 * 60 // 08:00
const GRID_END_MINUTES = 15 * 60 + 30 // 15:30

const LANG_FLAGS: Record<string, string> = { cz: '🇨🇿', en: '🇬🇧', de: '🇩🇪', pl: '🇵🇱' }
const LANG_LEVEL_ABBR: Record<string, string> = {
  [LanguageLevel.Basic]: 'A',
  [LanguageLevel.Intermediate]: 'B',
  [LanguageLevel.Proficient]: 'C',
}
const LICENCE_LEVEL_ABBR: Record<string, string> = {
  [LicenceLevel.ApulA]: 'A',
  [LicenceLevel.ApulB]: 'B',
  [LicenceLevel.ApulC]: 'C',
  [LicenceLevel.ApulD]: 'D',
}

const getSlotIndex = (time: string): number => {
  return TIME_SLOTS.indexOf(time)
}

const getColSpan = (startTime: string, endTime: string): number => {
  const startIdx = getSlotIndex(startTime)
  if (startIdx === -1) return 1
  const endIdx = getSlotIndex(endTime)
  if (endIdx !== -1) return Math.max(1, endIdx - startIdx)
  // endTime is outside TIME_SLOTS (e.g. "16:30", "17:00") — compute from time diff
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const slots = Math.round((eh * 60 + em - (sh * 60 + sm)) / 30)
  // Clamp to remaining grid columns
  return Math.max(1, Math.min(slots, TIME_SLOTS.length - startIdx))
}

/**
 * Compute vertical lane index for overlapping lessons (greedy interval colouring).
 * Returns a Map from lessonId → lane index (0-based).
 */
const computeLanes = (lessons: LessonDto[]): { lanes: Map<string, number>; laneCount: number } => {
  const sorted = [...lessons].sort((a, b) => a.startTime.localeCompare(b.startTime))
  const lanes = new Map<string, number>()
  // Each lane tracks the end time of the last placed lesson
  const laneEnds: string[] = []

  for (const lesson of sorted) {
    let placed = false
    for (let i = 0; i < laneEnds.length; i++) {
      if (lesson.startTime >= laneEnds[i]) {
        laneEnds[i] = lesson.endTime
        lanes.set(lesson.id, i)
        placed = true
        break
      }
    }
    if (!placed) {
      lanes.set(lesson.id, laneEnds.length)
      laneEnds.push(lesson.endTime)
    }
  }

  return { lanes, laneCount: laneEnds.length }
}

export const DayPlanGrid: FC<Props> = ({
  isToday,
  instructors,
  lessons,
  availabilityMap,
  selectedLessonId,
  onLessonClick,
  onEmptyCellClick,
  onLessonDrop,
  lessonPreview,
  pendingLessonIds,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { dragState, handlePointerDown, handlePointerMove, handlePointerUp, skipClickRef } = useLessonDrag(
    onLessonDrop,
    availabilityMap,
  )

  const unassignedLessons = useMemo(() => lessons.filter((l) => l.instructorIds.length === 0), [lessons])
  const assignedLessons = useMemo(() => lessons.filter((l) => l.instructorIds.length > 0), [lessons])

  const lessonMap = useMemo(() => {
    const map: Record<string, Record<string, LessonDto[]>> = {}
    for (const lesson of assignedLessons) {
      for (const instructorId of lesson.instructorIds) {
        if (!map[instructorId]) {
          map[instructorId] = {}
        }
        if (!map[instructorId][lesson.startTime]) {
          map[instructorId][lesson.startTime] = []
        }
        map[instructorId][lesson.startTime].push(lesson)
      }
    }
    return map
  }, [assignedLessons])

  const occupiedCells = useMemo(() => {
    const occupied = new Set<string>()
    for (const lesson of assignedLessons) {
      const startIdx = getSlotIndex(lesson.startTime)
      if (startIdx === -1) continue
      let endIdx = getSlotIndex(lesson.endTime)
      if (endIdx === -1) {
        // endTime outside TIME_SLOTS — compute from time difference
        const [sh, sm] = lesson.startTime.split(':').map(Number)
        const [eh, em] = lesson.endTime.split(':').map(Number)
        endIdx = startIdx + Math.round((eh * 60 + em - (sh * 60 + sm)) / 30)
      }
      const end = Math.min(endIdx, TIME_SLOTS.length)
      for (const instructorId of lesson.instructorIds) {
        for (let i = startIdx; i < end; i++) {
          occupied.add(`${instructorId}-${TIME_SLOTS[i]}`)
        }
      }
    }
    return occupied
  }, [assignedLessons])

  const { lanes: unassignedLanes, laneCount } = useMemo(() => computeLanes(unassignedLessons), [unassignedLessons])
  const unassignedRowHeight = laneCount > 0 ? laneCount * UNASSIGNED_LESSON_HEIGHT + 8 : 64

  const previewSlots = useMemo(() => {
    if (!lessonPreview?.startTime || !lessonPreview?.endTime) return null
    const startIdx = getSlotIndex(lessonPreview.startTime)
    if (startIdx === -1) return null
    const colSpan = getColSpan(lessonPreview.startTime, lessonPreview.endTime)
    const slots = new Set<string>()
    for (let i = startIdx; i < startIdx + colSpan && i < TIME_SLOTS.length; i++) {
      slots.add(TIME_SLOTS[i])
    }
    const hasInstructor = lessonPreview.instructorIds.length > 0
    return {
      slots,
      colSpan,
      startTime: lessonPreview.startTime,
      instructorIds: lessonPreview.instructorIds,
      hasInstructor,
    }
  }, [lessonPreview])

  const instructorHeaderRef = useRef<HTMLTableCellElement>(null)
  const [instrColWidth, setInstrColWidth] = useState(140)
  useEffect(() => {
    if (instructorHeaderRef.current) {
      setInstrColWidth(instructorHeaderRef.current.offsetWidth)
    }
  }, [instructors])

  const now = useCurrentTime()
  const timeLineLeft = useMemo(() => {
    if (!isToday) return null
    const minutes = now.getHours() * 60 + now.getMinutes()
    const minutesSince8am = minutes - GRID_START_MINUTES
    if (minutesSince8am < 0 || minutesSince8am > GRID_END_MINUTES - GRID_START_MINUTES) return null
    return instrColWidth + (minutesSince8am / 30) * SLOT_WIDTH
  }, [isToday, now, instrColWidth])

  return (
    <>
      <TableContainer
        component={Paper}
        sx={styles.gridContainer}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell ref={instructorHeaderRef} sx={styles.instructorHeaderCell} />
              {TIME_SLOTS.map((timeSlot) => (
                <TableCell
                  key={timeSlot}
                  sx={{
                    ...(styles.timeHeaderCell as object),
                    ...(previewSlots &&
                      !previewSlots.hasInstructor &&
                      previewSlots.slots.has(timeSlot) &&
                      (styles.highlightedTimeHeader as object)),
                  }}
                  data-time-header={timeSlot}
                >
                  {timeSlot}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {instructors.map((instructor) => (
              <TableRow key={instructor.id} sx={{ height: 64 }} data-instructor-id={instructor.id}>
                <TableCell sx={styles.instructorCell}>
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 'bold', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                      {instructor.name}
                    </Typography>
                    {(() => {
                      const langs = LANGUAGE_CODES.filter(
                        (c) => instructor.languages?.[c] && instructor.languages[c] !== LanguageLevel.None,
                      ).map((c) => `${LANG_FLAGS[c]}${LANG_LEVEL_ABBR[instructor.languages[c]]}`)
                      const lics = LICENCE_DISCIPLINES.filter(
                        (d) => instructor.licences?.[d] && instructor.licences[d] !== LicenceLevel.None,
                      ).map((d) => `${d === 'ski' ? '⛷️' : '🏂'}${LICENCE_LEVEL_ABBR[instructor.licences[d]]}`)
                      const parts = [...langs, ...lics]
                      if (parts.length === 0) return null
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.2 }}>
                          {parts.join('  ')}
                        </Typography>
                      )
                    })()}
                  </Box>
                </TableCell>
                {TIME_SLOTS.map((timeSlot) => {
                  const cellKey = `${instructor.id}-${timeSlot}`
                  const lessonsAtSlot = lessonMap[instructor.id]?.[timeSlot]

                  if (lessonsAtSlot?.length) {
                    // Use the widest lesson for cell sizing (colSpan)
                    const colSpan = Math.max(...lessonsAtSlot.map((l) => getColSpan(l.startTime, l.endTime)))
                    const isDropTarget =
                      dragState.phase === 'dragging' &&
                      dragState.dropTarget?.instructorId === instructor.id &&
                      dragState.dropTarget?.time === timeSlot
                    return (
                      <TableCell
                        key={cellKey}
                        colSpan={colSpan}
                        sx={{
                          p: 0,
                          position: 'relative',
                          width: colSpan * SLOT_WIDTH,
                          minWidth: colSpan * SLOT_WIDTH,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          ...(isDropTarget && { backgroundColor: 'rgba(25, 118, 210, 0.15)' }),
                        }}
                      >
                        {lessonsAtSlot.map((l) => {
                          const isCancelled = l.status === LessonStatus.Cancelled
                          const isCompleted = l.status === LessonStatus.Completed
                          const isBeingDragged =
                            dragState.phase === 'dragging' &&
                            dragState.lesson.id === l.id &&
                            dragState.sourceInstructorId === instructor.id
                          return (
                            <Box
                              key={l.id}
                              sx={{
                                ...(styles.lessonBlock(
                                  theme,
                                  l.product?.color,
                                  isCancelled,
                                  isCompleted,
                                  l.status,
                                  l.paymentStatus,
                                  l.id === selectedLessonId,
                                  getPaidRatio(l.bookings),
                                ) as object),
                                ...(isBeingDragged && { opacity: 0.3 }),
                                ...(pendingLessonIds?.has(l.id) && { opacity: 0.65, filter: 'grayscale(0.4)' }),
                              }}
                              onClick={() => {
                                if (skipClickRef.current) return
                                onLessonClick(l)
                              }}
                              onPointerDown={(e) => handlePointerDown(e, l, instructor.id)}
                            >
                              <LessonBlockContent lesson={l} />
                            </Box>
                          )
                        })}
                      </TableCell>
                    )
                  }

                  if (occupiedCells.has(cellKey)) {
                    return null
                  }

                  if (!isSlotAvailable(instructor.id, timeSlot, availabilityMap)) {
                    return <TableCell key={cellKey} sx={styles.unavailableCell} />
                  }

                  const isPreviewStart =
                    previewSlots?.hasInstructor &&
                    previewSlots.instructorIds.includes(instructor.id) &&
                    timeSlot === previewSlots.startTime
                  const isPreviewHighlight =
                    previewSlots && !previewSlots.hasInstructor && previewSlots.slots.has(timeSlot)
                  const isEmptyDropTarget =
                    dragState.phase === 'dragging' &&
                    dragState.dropTarget?.instructorId === instructor.id &&
                    dragState.dropTarget?.time === timeSlot

                  if (isPreviewStart) {
                    return (
                      <TableCell
                        key={cellKey}
                        sx={{
                          p: 0,
                          position: 'relative',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          overflow: 'visible',
                          width: SLOT_WIDTH,
                          minWidth: SLOT_WIDTH,
                        }}
                        onClick={() => onEmptyCellClick(instructor.id, timeSlot)}
                      >
                        <Box sx={{ ...styles.previewBlock, width: previewSlots.colSpan * SLOT_WIDTH }} />
                      </TableCell>
                    )
                  }

                  return (
                    <TableCell
                      key={cellKey}
                      sx={{
                        ...styles.emptyCell,
                        ...(isEmptyDropTarget && { backgroundColor: 'rgba(25, 118, 210, 0.15)' }),
                        ...(isPreviewHighlight && (styles.highlightedCell as object)),
                      }}
                      onClick={() => onEmptyCellClick(instructor.id, timeSlot)}
                    />
                  )
                })}
              </TableRow>
            ))}

            {/* Unassigned row */}
            <TableRow
              sx={{
                height: unassignedRowHeight,
                '& > td, & > th': { borderTop: '2px solid black' },
              }}
              data-instructor-id="unassigned"
            >
              <TableCell sx={styles.instructorCell}>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 'bold', whiteSpace: 'nowrap', fontStyle: 'italic', opacity: 0.6 }}
                >
                  {t('dayPlan.unassigned')}
                </Typography>
              </TableCell>
              {TIME_SLOTS.map((timeSlot) => {
                const isUnassignedDropTarget =
                  dragState.phase === 'dragging' &&
                  dragState.dropTarget?.instructorId === null &&
                  dragState.dropTarget?.time === timeSlot
                return (
                  <TableCell
                    key={`unassigned-${timeSlot}`}
                    sx={{
                      p: 0,
                      position: 'relative',
                      width: SLOT_WIDTH,
                      minWidth: SLOT_WIDTH,
                      borderBottom: '1px solid',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      verticalAlign: 'top',
                      ...(isUnassignedDropTarget && { backgroundColor: 'rgba(25, 118, 210, 0.15)' }),
                    }}
                  >
                    {/* Render lessons that START at this slot */}
                    {unassignedLessons
                      .filter((l) => l.startTime === timeSlot)
                      .map((lesson) => {
                        const colSpan = getColSpan(lesson.startTime, lesson.endTime)
                        const lane = unassignedLanes.get(lesson.id) ?? 0
                        const isCancelled = lesson.status === LessonStatus.Cancelled
                        const isBeingDragged = dragState.phase === 'dragging' && dragState.lesson.id === lesson.id
                        const color = lesson.product?.color ?? '#E0E0E0'
                        const isSelected = lesson.id === selectedLessonId
                        const bgStyle = styles.getStatusBackgroundStyle(
                          theme,
                          lesson.status,
                          lesson.paymentStatus,
                          isSelected,
                          getPaidRatio(lesson.bookings),
                        )
                        const statusHoverColor = styles.getStatusBackgroundColor(
                          theme,
                          lesson.status,
                          lesson.paymentStatus,
                        )

                        return (
                          <Box
                            key={lesson.id}
                            sx={{
                              position: 'absolute',
                              top: lane * UNASSIGNED_LESSON_HEIGHT + 4,
                              left: 0,
                              width: colSpan * SLOT_WIDTH,
                              height: UNASSIGNED_LESSON_HEIGHT - 4,
                              ...bgStyle,
                              borderTop: `4px solid ${color}`,
                              borderRadius: 1,
                              py: 0.5,
                              px: 1,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                              opacity: isCancelled ? 0.5 : 1,
                              textDecoration: isCancelled ? 'line-through' : 'none',
                              zIndex: 1,
                              '&:hover': {
                                backgroundColor: statusHoverColor + '50',
                              },
                              ...(lesson.id === selectedLessonId && { boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.5)' }),
                              ...(isBeingDragged && { opacity: 0.3 }),
                              ...(pendingLessonIds?.has(lesson.id) && { opacity: 0.65, filter: 'grayscale(0.4)' }),
                            }}
                            onClick={() => {
                              if (skipClickRef.current) return
                              onLessonClick(lesson)
                            }}
                            onPointerDown={(e) => handlePointerDown(e, lesson, null)}
                          >
                            <LessonBlockContent lesson={lesson} />
                          </Box>
                        )
                      })}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableBody>
        </Table>
        {timeLineLeft !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: timeLineLeft,
              width: 2,
              bgcolor: 'error.main',
              opacity: 0.7,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
      </TableContainer>
      <DragGhost dragState={dragState} />
    </>
  )
}
